import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dateAndTime from "date-and-time";
import { isArray } from "lodash";

import { usePymbleApi } from "./usePymbleApi";

export type TeacherAbsence = {
  Reason: string;
  StartAt: string;
  EndAt: string;
  ContactId: number;
  Source?: string | null | undefined;
};

// const API_END_POINT = `${api.dtAdmin}/teacher-absence`;
const API_END_POINT = "/teacher/absence/admin/"

const formatDate = (value: string | string[]) => {
  const datestr: string =
    isArray(value) && value.length > 0 ? value[0].toString() : value.toString();
  return dateAndTime.format(new Date(Date.parse(datestr)), "YYYY-MM-DD");
};

/**
 * Hook for separate loading of daily absence data (for teachers)
 * for components
 */
export function useTeacherAbsences(
  startDate: string | null = null,
  endDate: string | null = null
) {
  const [myAbsence, setMyAbsence] = useState<TeacherAbsence | null>(null);
  const [absenceErrors, setAbsenceErrors] = useState<any>(null);
  const [absenceSaving, setAbsenceSaving] = useState(false);
  const [teacherAbsences, setTeacherAbsences] = useState<any>([]);
  const [teacherAbsencesLoaded, setTeacherAbsencesLoaded] = useState(false);
  const [teacherAbsencesRange, setTeacherAbsencesRange] = useState<any>([]);
  const [teacherAbsencesRangeLoaded, setTeacherAbsencesRangeLoaded] =
    useState(false);

  const queryClient = useQueryClient();

  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();

  // Load the data from api
  let queryKey = ["teacher-absences", startDate];

  // Setup up our React Query Mutation for updating absence details
  const updateMyAbsenceMutation = useMutation({
    mutationFn: async (absence: TeacherAbsence) => {
      // Update my contact prefs via API
      let payload = absence;
      payload.Source = "Admin";
      const result = await callApi("PUT", API_END_POINT, {
        data: payload,
      });
      if (result?.status !== "ok" || result?.errors?.length > 0) {
        setAbsenceErrors(result?.errors);
      }
      return absence;
    },
    onSuccess: setMyAbsence,
  });

  // Setup a React Query Mutation for deleting absence details
  const deleteMyAbsenceMutation = useMutation({
    mutationFn: async (absenceId: string) => {
      const result = await callApi("DELETE", API_END_POINT + absenceId);
      return result;
    },
    onSuccess: () => {
      // Invalidate the query to refresh the data
      queryClient.invalidateQueries({ queryKey });
    },
  });

  /**
   * Save a teacher absence:
   *
   * @param absence: TeacherAbsence
   */
  const saveAbsence = async (absence: TeacherAbsence) => {
    let result = null;
    try {
      setAbsenceSaving(true);
      // Convert to format expected for save
      let newAbsence: TeacherAbsence = { ...absence };

      // Update teacher absence via API
      result = updateMyAbsenceMutation.mutate(newAbsence);
    } catch (error: any) {
      console.error(`ERROR: Could not save absence.  ${error?.message}`);
    } finally {
      setAbsenceSaving(false);
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  };

  /**
   * Delete a teacher absence:
   *
   * @param absenceId: string
   */
  const deleteAbsence = async (absenceId: string) => {
    let result = null;
    try {
      // Update teacher absence via API
      setAbsenceSaving(true);
      result = deleteMyAbsenceMutation.mutate(absenceId);
    } catch (error: any) {
      console.error(`ERROR: Could not delete absence.  ${error?.message}`);
    } finally {
      setAbsenceSaving(false);
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  }

  const { isLoading, isError, data, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const url =
        API_END_POINT +
        (startDate ? "?start_date=" + formatDate(startDate) : "") +
        (endDate ? "&end_date=" + endDate : "");
      const res = await callApi("GET", url);
      return res;
    },
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 1000 * 10, // 10 seconds
  });

  // When the data is loaded, set the state
  useEffect(() => {
    if (data) {
      let absences = preProcessAbsences(data);

      // Sort by date
      absences = sortAbsences(absences);

      if (absences) {
        setTeacherAbsences(absences);
      }
      setTeacherAbsencesLoaded(true);
    }
  }, [data]);

  const refreshTeacherAbsences = async () => {
    queryClient.invalidateQueries({ queryKey });
  };

  const refreshAbsenceRange = (start: string, end: string) => {
    let queryKeyRange = ["teacher-absences", start, end];

    queryClient.fetchQuery({
      queryKey: queryKeyRange,
      queryFn: async () => {
        const url =
          API_END_POINT +
          (start ? "?start_date=" + formatDate(start) : "") +
          (end ? "&end_date=" + formatDate(end) : "");
        let dataRange = await callApi("GET", url);

        let absences = preProcessAbsences(dataRange);

        // Sort by date
        absences = sortAbsences(absences);

        if (absences) {
          setTeacherAbsencesRange(absences);
        }
        setTeacherAbsencesRangeLoaded(true);

        return dataRange;
      },
      staleTime: 100,
    });
  };

  // Only sort by start time at the moment
  const sortAbsences = (data: any[]) => {
    return data.sort(function (a: any, b: any) {
      try {
        if (a && a.StartAt && b && b.StartAt) {
          var startA = a.StartAt;
          var startB = b.StartAt;
          if (startA < startB) {
            return -1;
          }
          if (startA > startB) {
            return 1;
          }
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        /* squash */
      }
      // names must be equal or documents incorrect
      return 0;
    });
  };

  // Decorate as required
  const preProcessAbsences = (absData: any) => {
    return absData.map((a: any) => {
      return a;
    });
  };

  return {
    isError,
    error,
    isLoading,
    teacherAbsences,
    teacherAbsencesRange,
    refreshTeacherAbsences,
    refreshAbsenceRange,
    teacherAbsencesLoaded,
    teacherAbsencesRangeLoaded,
    absenceErrors,
    absenceSaving,
    saveAbsence,
    deleteAbsence,
    myAbsence,
  };
}
